import type { IWalletPaymentRail, WalletFundProvider } from '../redux/buzzbridge/types';
import type { IPaymentRailResponse } from '../redux/payment/types';

/** `/payment/rail` returns the rail either nested under `payment_rail` or flattened at the root. */
export function resolvePaymentRail(
    response?: IPaymentRailResponse | IWalletPaymentRail | null,
): IWalletPaymentRail | undefined {
    if (!response) return undefined;
    const nested = (response as IPaymentRailResponse).payment_rail;
    if (nested) return nested;
    const flat = response as IWalletPaymentRail;
    const isRail =
        flat.preferred_provider !== undefined ||
        flat.use_paystack !== undefined ||
        flat.use_flutterwave !== undefined ||
        flat.use_stripe !== undefined;
    return isRail ? flat : undefined;
}

/** Contract: if (use_paystack) → Paystack. */
export function paystackCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    return Boolean(rail?.use_paystack && rail.paystack_available !== false);
}

/** Contract: else if (use_flutterwave) → Flutterwave (Africa outside Paystack markets). */
export function flutterwaveCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    return Boolean(rail?.use_flutterwave && rail.flutterwave_available !== false);
}

/** Contract: else if (use_stripe) → Stripe. */
export function stripeCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    if (!rail) return true;
    return rail.use_stripe !== false && rail.stripe_available !== false;
}

/** Prefer Paystack → Flutterwave → Stripe per backend rail contract. */
export function defaultCheckoutProvider(rail?: IWalletPaymentRail | null): WalletFundProvider {
    if (paystackCheckoutAvailable(rail)) return 'paystack';
    if (flutterwaveCheckoutAvailable(rail)) return 'flutterwave';
    return 'stripe';
}

/** Charge currency for create-payment-intent (minor units). Prefer charge_currency from rail. */
export function chargeCurrencyFromRail(rail?: IWalletPaymentRail | null, fallback = 'usd'): string {
    const raw =
        rail?.charge_currency ||
        rail?.paystack_currency ||
        rail?.flutterwave_currency ||
        rail?.payment_currency ||
        fallback;
    return String(raw).toLowerCase();
}

/** Region-specific Stripe.js key from the rail (EU vs US). */
export function stripePublishableKeyFromRail(rail?: IWalletPaymentRail | null): string | undefined {
    const key = rail?.stripe_publishable_key;
    return key ? String(key) : undefined;
}
